(ns ductile.locations.delivery-modalities
  "Parsing of German and Italian free-text delivery modalities into structured
  data. Works in both CLJ and CLJS.

  Main entry points:
    (parse text) — pure parsing, returns map with :delivery-hours, :night-delivery, :saturday-delivery
    (migration-txd db) — CLJ only, returns Datomic tx-data for bulk migration"
  (:require #?(:clj [ductile.datomic.api :as dd])
            [clojure.string :as str]))

;; ---------------------------------------------------------------------------
;; Day name normalization (German + Italian → OSM)
;; ---------------------------------------------------------------------------

(def ^:private day->osm
  "Maps lowercase day abbreviations (German and Italian) to OSM day codes."
  {;; German — 2-letter
   "mo" "Mo" "di" "Tu" "mi" "We" "do" "Th" "fr" "Fr" "sa" "Sa" "so" "Su"
   ;; German — full names
   "montag" "Mo" "dienstag" "Tu" "mittwoch" "We" "donnerstag" "Th"
   "freitag" "Fr" "samstag" "Sa" "sonntag" "Su"
   ;; Italian — 3-letter
   "lun" "Mo" "mar" "Tu" "mer" "We" "gio" "Th" "ven" "Fr" "sab" "Sa" "dom" "Su"
   ;; Italian — 2-letter
   "lu" "Mo" "ma" "Tu" "me" "We" "gi" "Th" "ve" "Fr"
   ;; Italian — full names (stripped of accents below)
   "lunedi"   "Mo" "martedi"  "Tu" "mercoledi" "We" "giovedi" "Th"
   "venerdi"  "Fr" "sabato"   "Sa" "domenica"  "Su"})

(defn- normalize-day [s]
  (-> s str/trim
      (str/replace #"[.\s']" "")
      (str/replace #"[ìí]" "i")
      (str/replace #"[àá]" "a")
      str/lower-case
      day->osm))

;; Regex alternation matching any recognised day token (German + Italian).
;; Ordered longest-first so the regex engine doesn't short-circuit on a prefix.
(def ^:private day-pattern
  "donnerstag|dienstag|mittwoch|montag|freitag|samstag|sonntag|mercoled[iì]|gioved[iì]|venerd[iì]|luned[iì]|marted[iì]|domenica|sabato|lun|mar|mer|gio|ven|sab|dom|mo|di|mi|do|fr|sa|so|lu|ma|me|gi|ve")

;; ---------------------------------------------------------------------------
;; Time parsing
;; ---------------------------------------------------------------------------

(defn- zero-pad [n]
  (let [s (str n)]
    (if (< (count s) 2) (str "0" s) s)))

(defn- parse-time-range
  "Parses 'HH:MM - HH:MM' or 'HH.MM-HH.MM' or 'HH:MM bis HH:MM' or
  'dalle HH:MM alle HH:MM' into 'HH:MM-HH:MM'."
  [s]
  (when-let [[_ h1 m1 h2 m2]
             (re-find #"(\d{1,2})[.:]\s*(\d{2})\s*(?:[-–]|bis|alle?)\s*(\d{1,2})[.:]\s*(\d{2})" s)]
    (str (zero-pad (parse-long h1)) ":" m1
         "-"
         (zero-pad (parse-long h2)) ":" m2)))

;; ---------------------------------------------------------------------------
;; Block parsing
;; ---------------------------------------------------------------------------

(def ^:private day-time-re
  "Matches a day-range followed by time(s), e.g. 'MO-FR: 08:00 - 18:00'
  or 'Lun-Ven 08:00-18:00'."
  (re-pattern (str "(?i)(" day-pattern ")[.'\\s]*[-–]?\\s*(" day-pattern ")?[.'\\s]*[:;\\s.]+(.+)")))

(defn- parse-day-time-block
  "Parses a single day-time block like 'MO-FR: 08:00 - 18:00' into 'Mo-Fr 08:00-18:00'.
  Also handles 'Lun-Ven 08:00-18:00' and similar Italian forms."
  [s]
  (when-let [[_ d1 d2 time-part] (re-find day-time-re s)]
    (when-let [time-range (parse-time-range time-part)]
      (let [day1 (normalize-day d1)
            day2 (when d2 (normalize-day d2))]
        (if day2
          (str day1 "-" day2 " " time-range)
          (str day1 " " time-range))))))

(def ^:private day-only-re
  "Matches lines with a day range but no time, e.g. 'Anlieferungen Mo - Sa'
  or 'Montag - Freitag'. Captures the two day tokens."
  (re-pattern (str "(?i)(?:anlieferung(?:en)?\\s+)?(" day-pattern ")[.'\\s]*[-–]\\s*(" day-pattern ")\\s*$")))

;; ---------------------------------------------------------------------------
;; Opening hours (top-level)
;; ---------------------------------------------------------------------------

(defn parse-opening-hours
  "Parses delivery hours text (German or Italian) into OSM opening_hours format.
  Returns nil if no hours could be parsed.
  Handles day+time on one line, and day-range on one line with time on a following line."
  [text]
  (let [lines (mapv str/trim (str/split-lines text))
        ;; First pass: try day+time blocks on single lines
        day-blocks (keep parse-day-time-block lines)]
    (if (seq day-blocks)
      (str/join "; " day-blocks)
      ;; Second pass: look for day-range line + time on a following line
      (let [n (count lines)]
        (loop [i 0, blocks []]
          (if (>= i n)
            (when (seq blocks) (str/join "; " blocks))
            (if-let [[_ d1 d2] (re-find day-only-re (nth lines i))]
              ;; Found a day-range line — scan following lines for a time range
              (let [day-str (str (normalize-day d1) "-" (normalize-day d2))
                    [time-range next-i]
                    (loop [j (inc i)]
                      (if (>= j n)
                        [nil j]
                        (if-let [tr (parse-time-range (nth lines j))]
                          [tr (inc j)]
                          (recur (inc j)))))]
                (if time-range
                  (recur next-i (conj blocks (str day-str " " time-range)))
                  (recur (inc i) blocks)))
              (recur (inc i) blocks))))))))

;; ---------------------------------------------------------------------------
;; Night delivery classification (German + Italian)
;; ---------------------------------------------------------------------------

(defn classify-night-delivery
  "Classifies night delivery policy from German or Italian free text.
  Returns :none, :possible, :only, or :unspecified."
  [t]
  (cond
    ;; German
    (re-find #"(?i)nur\s+nachtanlieferung" t) :only
    (re-find #"(?i)keine\s+nacht" t) :none
    (re-find #"(?i)nachtanl\S*\s+mögl" t) :possible
    (re-find #"(?i)nachtanl\S*\s+ok" t) :possible
    (re-find #"(?i)tag.*und.*nachtanl" t) :possible
    (re-find #"(?i)bei\s+nachtanl|bei\s+einer\s+nachtanl" t) :possible
    (re-find #"(?i)nachtanl\S*\s+bis" t) :possible
    (re-find #"(?i)nachtanl\S*\s+zwischen" t) :possible
    (re-find #"(?i)nachtanl\S*\s+von" t) :possible
    ;; Italian
    (re-find #"(?i)solo\s+consegn[ae]\s+notturn[ae]" t) :only
    (re-find #"(?i)no\s+consegn[ae]\s+notturn[ae]" t) :none
    (re-find #"(?i)nessuna\s+consegna\s+notturn[ae]" t) :none
    (re-find #"(?i)consegn[ae]\s+notturn[ae]\s+possibil[ei]" t) :possible
    (re-find #"(?i)consegn[ae]\s+notturn[ae]\s+disponibil[ei]" t) :possible
    (re-find #"(?i)anche\s+di\s+notte" t) :possible
    (re-find #"(?i)consegn[ae]\s+notturn[ae]" t) :possible
    :else :unspecified))

(defn night-delivery-ref
  "Returns the db/ident keyword for a night delivery classification, or nil for :unspecified."
  [classification]
  (case classification
    :none     :delivery.availability/none
    :possible :delivery.availability/possible
    :only     :delivery.availability/only
    nil))

;; ---------------------------------------------------------------------------
;; Saturday delivery classification (German + Italian)
;; ---------------------------------------------------------------------------

(defn classify-saturday-delivery
  "Classifies Saturday delivery from free text and parsed hours.
  Returns :none, :possible, :yes, or :unspecified."
  [text parsed-hours]
  (cond
    ;; Explicit no (German)
    (re-find #"(?i)keine\s+samstag" text) :none
    (re-find #"(?i)sa\+so\s+keine" text) :none
    ;; Explicit no (Italian)
    (re-find #"(?i)no\s+consegn[ae]\s+(?:il\s+)?sabato" text) :none
    (re-find #"(?i)nessuna\s+consegna\s+(?:il\s+)?sabato" text) :none
    ;; Explicit possible/by arrangement (German)
    (re-find #"(?i)anlieferung\s+sa\S*\s+mögl" text) :possible
    (re-find #"(?i)samstag\S*\s+mögl" text) :possible
    (re-find #"(?i)sa\S*\s+auf\s+vereinbarung" text) :possible
    ;; Explicit possible (Italian)
    (re-find #"(?i)consegn[ae]\s+(?:il\s+)?sabato\s+possibil[ei]" text) :possible
    (re-find #"(?i)anche\s+(?:il\s+)?sabato" text) :possible
    ;; Implicit yes from parsed hours containing Sa
    (and parsed-hours (re-find #"Sa " parsed-hours)) :yes
    :else :unspecified))

(defn saturday-delivery-ref
  "Returns the db/ident keyword for a Saturday delivery classification, or nil for :unspecified."
  [classification]
  (case classification
    :none     :delivery.availability/none
    :possible :delivery.availability/possible
    :yes      :delivery.availability/yes
    nil))

;; ---------------------------------------------------------------------------
;; Unified parse
;; ---------------------------------------------------------------------------

(defn parse
  "Parses German or Italian free-text delivery modalities into structured data.
  Returns a map (possibly empty) with optional keys:
    :location/delivery-hours    — OSM opening_hours string
    :location/night-delivery    — :delivery.availability/none | /possible | /only
    :location/saturday-delivery — :delivery.availability/none | /possible | /yes"
  [text]
  (when-not (str/blank? text)
    (let [night-class  (classify-night-delivery text)
          night-ref    (night-delivery-ref night-class)
          hours        (parse-opening-hours text)
          sat-class    (classify-saturday-delivery text hours)
          sat-ref      (saturday-delivery-ref sat-class)]
      (cond-> {}
        hours     (assoc :location/delivery-hours hours)
        night-ref (assoc :location/night-delivery night-ref)
        sat-ref   (assoc :location/saturday-delivery sat-ref)))))

;; ---------------------------------------------------------------------------
;; Migration (CLJ only — requires Datomic)
;; ---------------------------------------------------------------------------

#?(:clj
   (defn migrate-entry
     "Converts a single [eid text] pair into transaction data.
     Returns a vector of tx maps/assertions."
     [[eid text]]
     (if (str/blank? text)
       [[:db/retract eid :location/delivery-modalities text]]
       (let [parsed (parse text)]
         (if (seq parsed)
           [(assoc parsed :db/id eid)]
           [])))))

#?(:clj
   (defn unmigrated-entries
     "Returns [eid text] pairs for locations that have :location/delivery-modalities
     but none of the new structured attributes yet."
     [db]
     (dd/q '[:find ?e ?dm
             :where
             [?e :location/delivery-modalities ?dm]
             (not-join [?e]
                       (or [?e :location/delivery-hours]
                           [?e :location/night-delivery]
                           [?e :location/saturday-delivery]))]
           db)))

#?(:clj
   (defn migration-txd
     "Builds transaction data for migrating unmigrated :location/delivery-modalities
     into structured attributes. Idempotent — skips already-migrated entities.
     Suitable for use from interventions or REPL."
     [db]
     (vec (mapcat migrate-entry (unmigrated-entries db)))))
